// SUB-OFFICE 7 — THE CAB (the universal transition mask). Increment M-B /
// graphics Increment 8. The elevator cab is the diegetic loading screen: the
// doors close (step-function — mechanisms do not ease), the split-flap floor
// indicator flaps through floors, and under the closed doors the target zone
// builds and the previous one disposes; the doors open on the new zone. NO
// spinner exists, still (spec Increment 8).
//
// The cab is implemented as a diegetic DOM overlay rather than a 3D sub-scene:
// an opaque overlay is the ONLY mask that reliably covers the single-frame
// build hitch regardless of frame timing (a 3D cab would itself hitch while
// its target builds). It is styled to canon — bone wainscot walls, a brass
// control plate with hex bolts and one amber pilot per destination, the
// split-flap indicator. M-D may upgrade this to a walk-in 3D cab; the mask
// contract (ride() resolves when the doors have opened on the destination) is
// what the ZoneManager depends on and would not change.
//
// It also carries the RATIFIED Q5 unbidden-stop doctrine as CODE: the cab may,
// rarely, stop unbidden — but never by pure RNG. It fires only while a hidden
// MARK is set (arm()), is HARD RATE-LIMITED to at most once per multi-day
// window, opens on a cheap dead-end beat, and reclose after N seconds. For M-B
// the mark is never set by default; the plumbing ships so Wave-2 (Aversion
// Protocol / Bureau Calendar) can arm it.

import machineFloor from '../../../content/machine-floor.json';

const UNBIDDEN_KEY = 'so7_unbidden_ts';
const UNBIDDEN_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // hard rate-limit: once per 3 days

/** The line the cab shows on an unbidden stop (content, verbatim). */
const UNBIDDEN_LINE = machineFloor.verticalCore.elevatorLines[5]; // "THIS STOP WAS NOT REQUESTED. IT IS REQUIRED."

export interface CabRide {
  /** split-flap start label (current floor). */
  fromLabel: string;
  /** split-flap settle label (destination floor). */
  toLabel: string;
  descend: boolean;
  /**
   * Swap the world under the closed doors: dispose the previous streamed scene,
   * build + install the target (scene add, collision grid, spawn). Runs exactly
   * once, while the doors are shut and the overlay is fully opaque.
   */
  onSwap: () => void;
}

const delay = (ms: number): Promise<void> => new Promise((r) => window.setTimeout(r, ms));

export class CabMask {
  private mark = false;
  private busy = false;

  constructor(private reducedMotion: boolean) {}

  /** Q5: set/clear the hidden mark that permits an unbidden stop (Wave-2). */
  arm(on: boolean): void { this.mark = on; }

  /** True while a ride is in progress (guards re-entry). */
  get riding(): boolean { return this.busy; }

  private rateLimitOk(): boolean {
    try {
      const last = Number(localStorage.getItem(UNBIDDEN_KEY) || '0');
      return Date.now() - last > UNBIDDEN_WINDOW_MS;
    } catch { return false; }
  }
  private stampUnbidden(): void {
    try { localStorage.setItem(UNBIDDEN_KEY, String(Date.now())); } catch { /* private mode */ }
  }

  /** Whether THIS ride would insert the unbidden stop (mark set + rate-limit ok). */
  private wantUnbidden(): boolean { return this.mark && this.rateLimitOk(); }

  /**
   * Ride the cab. Resolves once the doors have opened on the destination — the
   * caller's onSwap has run and the target zone is live. Masks any hitch.
   */
  async ride(r: CabRide): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const rm = this.reducedMotion;
    const ui = this.mount(r.fromLabel);

    // 1) doors close (step-function). The overlay is already opaque; this is
    //    the visible "shut" beat over the top of it.
    ui.setDoors(false);
    await delay(rm ? 60 : 340);

    // 2) under the closed doors: swap the world (build/dispose/grid/spawn).
    let swapped = false;
    const doSwap = (): void => { if (!swapped) { swapped = true; r.onSwap(); } };

    // 3) the ride: the split-flap flaps through floors. The swap fires at the
    //    start of the flap so the build finishes well before the doors open.
    ui.hum(r.descend);
    doSwap();
    await ui.flap(r.fromLabel, r.toLabel, rm);

    // 4) the unbidden stop (Q5) — conditioned, rate-limited, never pure RNG.
    if (this.wantUnbidden()) {
      this.stampUnbidden();
      ui.setFlap('—');
      ui.say(UNBIDDEN_LINE);
      ui.setDoors(true);          // doors crack on a dark, silent beat
      await delay(rm ? 400 : 2200);
      ui.say('');
      ui.setDoors(false);         // reclose; the ride resumes to the real floor
      await delay(rm ? 60 : 320);
      await ui.flap('—', r.toLabel, rm);
    }

    // 5) doors open on the new zone; lift the overlay.
    ui.setDoors(true);
    await delay(rm ? 60 : 300);
    ui.unmount();
    this.busy = false;
  }

  // --- the DOM cab -----------------------------------------------------------

  private mount(startLabel: string) {
    const root = document.createElement('div');
    root.setAttribute('data-cab', '');
    root.style.cssText =
      'position:fixed;inset:0;z-index:9998;pointer-events:none;overflow:hidden;' +
      'background:#1c1a17;font-family:ui-monospace,monospace';

    // cab interior backdrop: bone wainscot + a warm gas ceiling wash
    const inner = document.createElement('div');
    inner.style.cssText =
      'position:absolute;inset:0;background:' +
      'linear-gradient(#e9e4d8 0%,#ddd6c6 46%,#ccc4b0 100%);' +
      'box-shadow:inset 0 60px 120px rgba(237,244,220,0.25),inset 0 -40px 120px rgba(28,26,23,0.35)';
    root.appendChild(inner);

    // brass control plate with hex bolts + one amber pilot per destination
    const plate = document.createElement('div');
    plate.style.cssText =
      'position:absolute;right:8%;top:34%;width:88px;height:200px;border-radius:6px;' +
      'background:linear-gradient(#9c7e3c,#7c6330);box-shadow:0 2px 10px rgba(0,0,0,0.4)';
    for (let i = 0; i < 4; i++) {
      const pilot = document.createElement('div');
      const lit = i === 1;
      pilot.style.cssText =
        `position:absolute;left:20px;top:${18 + i * 44}px;width:16px;height:16px;border-radius:50%;` +
        `background:${lit ? '#d99321' : '#3a2f18'};` + (lit ? 'box-shadow:0 0 10px #d99321' : '');
      plate.appendChild(pilot);
      const bolt = document.createElement('div');
      bolt.style.cssText =
        `position:absolute;right:14px;top:${20 + i * 44}px;width:10px;height:10px;` +
        'background:#6b5426;clip-path:polygon(25% 0,75% 0,100% 50%,75% 100%,25% 100%,0 50%)';
      plate.appendChild(bolt);
    }
    inner.appendChild(plate);

    // the split-flap floor indicator (above the door)
    const flap = document.createElement('div');
    flap.style.cssText =
      'position:absolute;left:50%;top:16%;transform:translateX(-50%);' +
      'min-width:96px;padding:10px 18px;text-align:center;border-radius:4px;' +
      'background:#161412;color:#d99321;font-weight:700;font-size:64px;line-height:1;' +
      'box-shadow:inset 0 3px 6px rgba(0,0,0,0.7)';
    flap.textContent = startLabel;
    inner.appendChild(flap);

    // the Machine's voice, low on the plate
    const voice = document.createElement('div');
    voice.style.cssText =
      'position:absolute;left:6%;right:6%;bottom:9%;text-align:center;color:#d99321;' +
      'font-size:15px;letter-spacing:0.06em;min-height:20px;opacity:0.92';
    inner.appendChild(voice);

    // two cab doors that slide over the interior (step-function slide)
    const mkDoor = (side: 'left' | 'right'): HTMLElement => {
      const d = document.createElement('div');
      d.style.cssText =
        `position:absolute;top:0;bottom:0;${side}:0;width:50%;` +
        'background:linear-gradient(90deg,#cfc7b3,#e2dccd);' +
        `border-${side === 'left' ? 'right' : 'left'}:2px solid #9c7e3c;` +
        'transition:transform 0s;will-change:transform';
      return d;
    };
    const left = mkDoor('left');
    const right = mkDoor('right');
    root.appendChild(left);
    root.appendChild(right);

    document.body.appendChild(root);

    const setDoors = (open: boolean): void => {
      const dur = this.reducedMotion ? '0s' : '0.32s';
      left.style.transition = `transform ${dur} steps(6,end)`;
      right.style.transition = `transform ${dur} steps(6,end)`;
      left.style.transform = open ? 'translateX(-100%)' : 'translateX(0)';
      right.style.transform = open ? 'translateX(100%)' : 'translateX(0)';
    };
    // start OPEN (you are stepping in), then ride() closes them
    setDoors(true);

    const setFlap = (label: string): void => { flap.textContent = label; };
    const say = (line: string): void => { voice.textContent = line; };
    const hum = (_descend: boolean): void => { /* audio hook lands with the SFX pass */ };

    const flapTo = async (from: string, to: string, rm: boolean): Promise<void> => {
      if (rm) { flap.textContent = to; return; }
      // flap through a few filler frames then settle (the split-flap 'settle')
      const frames = ['-', from, '·', to, from, to];
      for (const f of frames) { flap.textContent = f; await delay(70); }
      flap.textContent = to;
      await delay(160);
    };

    return {
      setDoors, setFlap, say, hum,
      flap: flapTo,
      unmount: (): void => { root.remove(); },
    };
  }
}
